import { PropsWithClassName } from "@/types/ui";
import type { VariantProps } from "class-variance-authority";
import React from "react";
import { Badge, badgeVariants } from "@components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";
import { cn } from "@lib/utils";
import { useTranslateProposalFields } from "@features/proposals/hooks/proposal-translate-fields";
import { proposalsTools } from "@features/proposals/proposals-tools";
import { ProposalAggWorkflowOutputView } from "@features/proposals/types/proposal";

/**
 * ProposalWorkflowStatusBadge
 *
 * Purpose:
 * - Renders a small, color-coded badge with a localized label for the current proposal workflow status.
 * - Shows a tooltip with a more detailed, localized description of that status.
 *
 * Business rules:
 * - If the proposal has no workflow status, nothing is rendered (returns null) to avoid displaying misleading UI.
 * - Closed proposals (as defined by proposalsTools.filters.isClosedProposal) use a "secondary" background color,
 *   while non-closed proposals use an emerald tone to visually distinguish ongoing vs. terminal states.
 * - The textual label and description are obtained from useTranslateProposalFields to respect current locale.
 *
 * Props:
 * - `proposal`: must contain `workflow.status`; otherwise the component does not render.
 * - `variant`: forwarded to Badge; defaults to "outline" to blend with header contexts.
 * - `className`: allows consumers to size/position the badge in toolbars or headers.
 *
 * Accessibility/UX:
 * - Tooltip provides additional context for non-obvious statuses without cluttering the badge.
 * - The badge content is strictly textual for screen readers; color is an auxiliary cue.
 */
export const ProposalWorkflowStatusBadge = ({
  proposal,
  mode = "speaker",
  className,
  ...props
}: {
  proposal: { workflow?: ProposalAggWorkflowOutputView };
  mode?: "speaker" | "team" | "badge";
} & VariantProps<typeof badgeVariants> &
  PropsWithClassName) => {
  const { translateWorkflowStatus } = useTranslateProposalFields();

  // Determine whether the workflow status is considered "closed" per business constants.
  // This drives the badge's background color and helps users scan terminal items quickly.
  const isClosed = proposalsTools.filters.isClosedProposal(proposal);

  // Defensive guard: without a workflow status, we cannot present a meaningful badge.
  // Early return keeps the header clean and avoids null/undefined translations.
  if (!proposal?.workflow?.status) {
    return null;
  }

  // Localize both short label and extended description for the tooltip.
  const { label, descriptionSpeaker, descriptionTeam } = translateWorkflowStatus(
    proposal.workflow?.status,
  );

  if (mode === "badge") {
    return (
      <Badge
        // Keep visual style predictable; "outline" is a safe default for mixed-light contexts.
        variant="outline"
        // cn() merges conditional styles with external className.
        // - Closed -> subdued "secondary" background.
        // - Active -> emerald background (light/dark aware).
        {...props}
        className={cn(
          "cursor-default",
          { "bg-secondary": isClosed, "bg-emerald-300 dark:bg-emerald-500": !isClosed },
          className,
        )}
      >
        <span className="">{label}</span>
      </Badge>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          // Keep visual style predictable; "outline" is a safe default for mixed-light contexts.
          variant="outline"
          // cn() merges conditional styles with external className.
          // - Closed -> subdued "secondary" background.
          // - Active -> emerald background (light/dark aware).
          {...props}
          className={cn(
            "cursor-pointer",
            { "bg-secondary": isClosed, "bg-emerald-300 dark:bg-emerald-500": !isClosed },
            className,
          )}
        >
          <span className="">{label}</span>
        </Badge>
      </TooltipTrigger>
      {/* Tooltip conveys the longer description; useful when short labels are ambiguous for end users. */}
      <TooltipContent>
        {mode === "speaker" ? descriptionSpeaker : mode === "team" ? descriptionTeam : ""}
      </TooltipContent>
    </Tooltip>
  );
};
